import {
  Component,
  OnInit,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  NgZone,
  OnDestroy
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ActivatedRoute, Router } from '@angular/router';
import { ZoomService } from 'src/app/services/zoom.service';
import { VideoWebRtcComponent } from 'src/app/components/video-web-rtc/video-web-rtc.component';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { SocketWebRtcService } from 'src/app/services/socket-web-rtc.service';
import { ScheduleService } from 'src/app/services/schedule.service';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FunctionsService } from 'src/app/services/functions.service';
import { FichaMedicaService } from 'src/app/services/ficha-medica.service';
import { DocumentsService } from 'src/app/services/documents.service';
import { Subject, timer, of } from 'rxjs';
import { switchMap, takeUntil, filter, catchError } from 'rxjs/operators';
import { URL_SERVICIOS } from 'src/app/config/config';
import { HttpClient, HttpHeaders } from '@angular/common/http';

interface Cita {
  id: number;
  fecha: string;
  hora: string;
  duracionMin: number;
  paciente: string;
  medico: string;
  especialidad: string;
  estado: 'confirmada' | 'pendiente' | 'cancelada';
}

interface Slot {
  hora: string;
  cita?: Cita | null;
}

@Component({
  selector: 'app-videoconsulta-doctor',
  templateUrl: './videoconsulta-doctor.component.html',
  styleUrls: ['./videoconsulta-doctor.component.css']
})
export class VideoconsultaDoctorComponent implements OnInit, OnDestroy {

  selectedDate: string = this.toISODate(new Date());
  horaInicio = 8;
  horaFin = 18;
  intervaloMin = 60;

  // ======================================================
  // CONFIGURACIÓN ANULACIÓN
  // ======================================================

  /*
   * PRODUCCIÓN: 10 minutos.
   * Para pruebas puntuales puedes activar MODO_PRUEBA_ANULACION y
   * modificar temporalmente TIEMPO_ESPERA_ANULACION_MS.
   */
  private readonly MODO_PRUEBA_ANULACION = false;
  private readonly TIEMPO_ESPERA_ANULACION_MS = 10 * 60 * 1000;
  private fechaInicioPruebaAnulacion: number | null = null;

  citas: Cita[] = [
    {
      id: 1,
      fecha: this.toISODate(new Date()),
      hora: '09:00',
      duracionMin: 30,
      paciente: 'Juan Pérez',
      medico: 'Dra. Soto',
      especialidad: 'Medicina General',
      estado: 'confirmada'
    },
    {
      id: 2,
      fecha: this.toISODate(new Date()),
      hora: '11:00',
      duracionMin: 30,
      paciente: 'María Gómez',
      medico: 'Dr. Rivas',
      especialidad: 'Pediatría',
      estado: 'pendiente'
    },
    {
      id: 3,
      fecha: this.addDaysISO(new Date(), 1),
      hora: '10:00',
      duracionMin: 30,
      paciente: 'Luis Díaz',
      medico: 'Dra. Pino',
      especialidad: 'Nutrición',
      estado: 'cancelada'
    }
  ];

  slots: Slot[] = [];

  private _selectedView:
    'consulta' |
    'video' |
    'paciente' = 'consulta';

  get selectedView():
    'consulta' |
    'video' |
    'paciente' {
    return this._selectedView;
  }

  set selectedView(
    value: 'consulta' | 'video' | 'paciente'
  ) {
    if (this._selectedView === value) return;

    this._selectedView = value;

    if (value !== 'paciente') {
      this.activePacienteTab = 'consultasprevias';
    }
  }

  activePacienteTab:
    'antecedentes' |
    'consultasprevias' |
    'documentos' = 'consultasprevias';

  activeConsultaTab:
    'consulta' |
    'agenda' = 'consulta';

  selectedDocTab = 'Anamnesis';
  selectedTabDocs = '';

  // ======================================================
  // FINALIZACIÓN / CANCEL REASON
  // ======================================================

  modalActual: string | null = null;

  anularConsultaForm: FormGroup;

  cancelReason: any[] = [];

  /*
   * REGLA type_end:
   *
   * 1 = Finalización normal.
   * 2 = Paciente no asiste.
   * 3 = Otro problema / motivo.
   */
  typeEndSeleccionado: 1 | 2 | 3 = 1;

  /*
   * Aquí guardamos el id_reason real proveniente
   * de cancel_reason.
   */
  cancelReasonSeleccionadoId: number | null = null;

  finalizacionDesdeAnulacion = false;

  // ======================================================
  // VIDEOLLAMADA
  // ======================================================

  img_paciente = '';

  videoIniciado = false;
  videollamadaFueIniciada = false;
  videoFinalizada = false;
  showVideo = false;
  btnIniciar = false;

  puedeAnularConsulta = false;

  get videollamadaYaIniciada(): boolean {
    return this.videollamadaFueIniciada;
  }

  /**
   * Fuente única para determinar la modalidad.
   * Idealmente el backend siempre debería retornar boolean, pero aquí
   * toleramos true/1/'1'/'true' para evitar desvíos de flujo.
   */
  get esOnDemand(): boolean {
    const value = this.consulta?.tel_doc_app_boo_is_ondemand;
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
  }

  /**
   * El único estado no operable conocido por este componente es 39.
   * No se agregan otros IDs sin conocer el catálogo real del backend.
   */
  get consultaOperable(): boolean {
    return !!this.consulta?.id_consulta && Number(this.consulta?.tel_doc_app_boo_sta_id) !== 39;
  }

  get mostrarBotonFinalizarConsulta(): boolean {
    return this.consultaOperable && !this.videoIniciado && this.videollamadaYaIniciada;
  }

  get mostrarBotonIniciarVideollamada(): boolean {
    return this.consultaOperable && !this.videoIniciado;
  }

  get mostrarBotonAnularConsulta(): boolean {
    return this.consultaOperable &&
      this.videollamadaYaIniciada &&
      this.puedeAnularConsulta && !this.videoIniciado;
  }

  consulta: any = {
    fecha_consulta: {}
  };

  id_de_consulta = '';
  _usuario: any = {};
  entro_anfitrion: any;

  isLoading = false;

  infoConsulta: any = {};
  estado_consulta: any = {};

  archivos_consulta: any = {
    diagnostics: [],
    certificados: [],
    prescriptions: [],
    orders: []
  };

  documentos_consulta: any = [];

  _docCheckModalVL = false;

  mostrarModalInstrucciones = false;
  aceptoInstrucciones = false;

  @ViewChild(
    'WEB_RTC',
    { static: false }
  )
  WEB_RTC!: VideoWebRtcComponent;

  @ViewChild('modalInstruccionesTpl')
  modalInstruccionesTpl!: TemplateRef<any>;

  private overlayRef?: OverlayRef;

  private destroy$ =
    new Subject<void>();

  private observerStarted = false;

  pacienteTieneProblemaMedia = false;

  pacienteProblemaMediaCode:
    string |
    null = null;

  pacienteProblemaMediaDetail:
    string |
    null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private _zoomService: ZoomService,
    private web_rtc_socket: SocketWebRtcService,
    private _schedule: ScheduleService,
    private fb: FormBuilder,
    private router: Router,
    private _functions: FunctionsService,
    private _fichaMedicaServices: FichaMedicaService,
    private _documentServices: DocumentsService,
    private http: HttpClient,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private zone: NgZone
  ) {

    /*
     * motivo:
     * obligatorio.
     *
     * end_description:
     * opcional.
     */
    this.anularConsultaForm =
      this.fb.group({
        paciente: [
          null,
          Validators.required
        ],

        id_consulta: [
          null,
          Validators.required
        ],

        motivo: [
          null,
          Validators.required
        ],

        end_description: [
          null
        ]
      });
  }

  ngOnInit(): void {

    this.activeConsultaTab =
      'consulta';

    this.activePacienteTab =
      'consultasprevias';

    this._usuario =
      new EncriptacionService()
        .getUserInfo();

    if (
      this._usuario?.typ_id != 1
    ) {
      Swal.fire({
        icon: 'info',
        title: 'Acceso no autorizado',
        timer: 2000
      });

      setTimeout(
        () =>
          this.router.navigate(
            ['dashboard']
          ),
        2000
      );

      return;
    }

    this._docCheckModalVL =
      this._usuario?.doc_check_modal_vl;

    this.aceptoInstrucciones =
      this._docCheckModalVL === true;

    if (!this.aceptoInstrucciones) {

      const key =
        'ocultarModalInstruccionesDoctor' +
        this._usuario._id;

      this.aceptoInstrucciones =
        localStorage.getItem(key) === 'true';
    }

    this.generarSlots();
    this.sincronizarCitasEnSlots();

    localStorage.setItem(
      'zoom_up_med',
      'true'
    );

    this.activatedRoute.params
      .subscribe(params => {

        this.resetearEstadoConsulta();

        this.id_de_consulta =
          params.idConsulta;

        this.cargarEstadoVideollamada();

        this.loadMediaIssue(
          String(
            this.id_de_consulta
          )
        );

        this.obtenerConsulta(
          this.id_de_consulta
        );

        this.startObservadorWhenReady();
      });

    /*
     * Revisión frontend cada segundo.
     * No realiza llamadas adicionales al backend.
     */
    timer(0, 1000)
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe(() =>
        this.actualizarDisponibilidadAnulacion()
      );

    this.listenPatientWebRtcStatus();

    /*
     * Motivos disponibles exclusivamente
     * para CONSULTA-MEDICO.
     */
    this.cargarMotivosCancelacion();
  }

  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();

    this.cerrarOverlay();

    document.body.classList.remove(
      'modal-open'
    );
  }

  private resetearEstadoConsulta(): void {

    this.videoIniciado = false;
    this.videollamadaFueIniciada = false;
    this.videoFinalizada = false;

    this.btnIniciar = false;
    this.puedeAnularConsulta = false;

    this.fechaInicioPruebaAnulacion =
      null;

    this.estado_consulta = {};

    this.modalActual = null;

    this.mostrarModalInstrucciones =
      false;

    this.typeEndSeleccionado = 1;

    this.cancelReasonSeleccionadoId =
      null;

    this.finalizacionDesdeAnulacion =
      false;

    this.cerrarOverlay();

    this.anularConsultaForm.reset({
      paciente: null,
      id_consulta: null,
      motivo: null,
      end_description: null
    });
  }

  // ======================================================
  // FECHAS / REGLA ANULACIÓN
  // ======================================================

  private parseFechaHora(
    value: any
  ): Date | null {

    if (!value) {
      return null;
    }

    if (
      value instanceof Date
    ) {
      return isNaN(
        value.getTime()
      )
        ? null
        : value;
    }

    const texto =
      String(value).trim();

    if (!texto) {
      return null;
    }

    /*
     * Si recibimos solamente:
     *
     * 13:06:05
     *
     * no podemos crear una fecha completa.
     */
    if (
      /^\d{1,2}:\d{2}/
        .test(texto)
    ) {
      return null;
    }

    /*
     * PostgreSQL:
     *
     * 2026-09-07 13:06:05
     *
     * Javascript:
     *
     * 2026-09-07T13:06:05
     */
    const fecha =
      new Date(
        texto.replace(
          ' ',
          'T'
        )
      );

    return isNaN(
      fecha.getTime()
    )
      ? null
      : fecha;
  }

  /*
   * RESERVA / AGENDADA:
   * usa la fecha/hora agendada de la consulta.
   *
   * ONDEMAND:
   * usa el inicio REAL de la videollamada informado por backend
   * en postMeetingState.zoo_mee_start_meeting.
   */
  private obtenerFechaInicioAnulacion(): Date | null {

    /*
     * ======================================================
     * AGENDAMIENTO
     * ======================================================
     *
     * Los 10 minutos se cuentan desde la hora agendada.
     *
     * IMPORTANTE:
     * Que hayan pasado los 10 minutos NO basta para mostrar
     * el botón. El getter mostrarBotonAnularConsulta también
     * exige que la videollamada haya sido iniciada al menos
     * una vez.
     */
    if (!this.esOnDemand) {

      if (
        this.MODO_PRUEBA_ANULACION &&
        this.fechaInicioPruebaAnulacion
      ) {
        return new Date(
          this.fechaInicioPruebaAnulacion
        );
      }

      return this.parseFechaHora(
        this.consulta?.fecha_consulta?.date
      );
    }

    /*
     * ======================================================
     * ATENCIÓN INMEDIATA / ONDEMAND
     * ======================================================
     *
     * Los 10 minutos comienzan desde el PRIMER inicio
     * de la videollamada.
     *
     * getVideoStartedAtKey() mantiene ese primer timestamp
     * aunque posteriormente se finalice y reinicie el video.
     */
    const startedAtRaw =
      localStorage.getItem(
        this.getVideoStartedAtKey()
      );

    if (startedAtRaw) {

      const startedAt =
        Number(startedAtRaw);

      if (
        Number.isFinite(startedAt) &&
        startedAt > 0
      ) {
        return new Date(startedAt);
      }
    }

    /*
     * Fallback después de un F5 o si no existe el dato local.
     *
     * Se intenta recuperar el inicio desde postMeetingState.
     */
    const fechaInicioBackend =
      this.parseFechaHora(
        this.estado_consulta?.zoo_mee_start_meeting
      );

    if (fechaInicioBackend) {

      this.guardarVideollamadaIniciada(
        fechaInicioBackend
      );

      return fechaInicioBackend;
    }

    /*
     * Si nunca se inició la videollamada OnDemand,
     * todavía no existe inicio para el contador.
     */
    return null;
  }

  private actualizarDisponibilidadAnulacion(): void {

    if (!this.consulta?.id_consulta) {
      this.puedeAnularConsulta = false;
      return;
    }

    const fechaInicio =
      this.obtenerFechaInicioAnulacion();

    if (!fechaInicio) {
      this.puedeAnularConsulta = false;
      return;
    }

    const fechaHabilitacion =
      fechaInicio.getTime() +
      this.TIEMPO_ESPERA_ANULACION_MS;

    this.puedeAnularConsulta =
      Date.now() >= fechaHabilitacion;
  }

  // ======================================================
  // VISTAS
  // ======================================================

  onSelectedTabFromDocumentos(
    tabKey: string
  ): void {

    this.selectedDocTab =
      tabKey;
  }

  onWebRtcLog(
    msg: string
  ): void {
  }

  changeView(
    view:
      'consulta' |
      'video' |
      'paciente'
  ): void {

    this.selectedView =
      view;

    this.activePacienteTab =
      'consultasprevias';
  }

  onToggleViewFromVideo():
    void {

    this.selectedView =
      this.selectedView === 'video'
        ? 'consulta'
        : 'video';
  }

  // ======================================================
  // POST MEETING STATE
  // ======================================================

  private startObservador():
    void {

    const httpOptions = {
      headers:
        new HttpHeaders({
          authorization:
            sessionStorage
              .getItem('token') ||
            '',

          'Content-Type':
            'application/json'
        })
    };

    const urlPost =
      URL_SERVICIOS +
      '/postMeetingState';

    timer(
      0,
      5000
    )
      .pipe(

        takeUntil(
          this.destroy$
        ),

        filter(() =>
          !!this.id_de_consulta &&
          !!this.consulta
            ?.user_id
        ),

        switchMap(() =>
          this.http
            .post<any[]>(
              urlPost,

              {
                id_room:
                  this.id_de_consulta,

                user_id:
                  this.consulta.user_id
              },

              httpOptions
            )
            .pipe(

              catchError(
                error => {

                  return of([]);
                }
              )
            )
        )
      )
      .subscribe(
        (data: any[]) => {
          if (!data?.length) {
            return;
          }

          this.estado_consulta = data[0];

          /*
           * El backend es la fuente de verdad respecto de si la videollamada
           * ya comenzó. Esto permite recuperar correctamente el estado tras
           * un F5, cambio de vista o pérdida temporal de estado local.
           */
          this.sincronizarEstadoVideollamadaDesdeBackend();

          /*
           * Recalcula la anulación. En OnDemand depende de
           * zoo_mee_start_meeting; en Reserva, de la hora agendada.
           */
          this.actualizarDisponibilidadAnulacion();

          /*
           * IMPORTANTE: el polling NO finaliza la reunión automáticamente.
           * Finalizar videollamada debe depender de una acción explícita del
           * médico o de un evento WebRTC confiable, nunca de videoIniciado=false
           * luego de un refresh.
           */
        }
      );
  }

  private sincronizarEstadoVideollamadaDesdeBackend(): void {
    const fechaInicioReal = this.parseFechaHora(this.estado_consulta?.zoo_mee_start_meeting);

    if (!fechaInicioReal) {
      return;
    }

    this.videollamadaFueIniciada = true;
    this.guardarVideollamadaIniciada(fechaInicioReal);
  }

  private startObservadorWhenReady():
    void {

    if (
      this.observerStarted
    ) {
      return;
    }

    if (
      !this.id_de_consulta
    ) {
      return;
    }

    if (
      !this.consulta
        ?.user_id
    ) {
      return;
    }

    this.observerStarted =
      true;

    this.startObservador();
  }

  // ======================================================
  // AGENDA MOCK
  // ======================================================

  onFechaChange():
    void {

    this.sincronizarCitasEnSlots();
  }

  private generarSlots():
    void {

    const slots:
      Slot[] = [];

    for (
      let h =
        this.horaInicio;
      h <
      this.horaFin;
      h++
    ) {
      slots.push({
        hora:
          this.pad2(h) +
          ':00',

        cita:
          null
      });
    }

    this.slots =
      slots;
  }

  private sincronizarCitasEnSlots():
    void {

    this.slots.forEach(
      slot =>
        slot.cita =
        null
    );

    const delDia =
      this.citas.filter(
        cita =>
          cita.fecha ===
          this.selectedDate
      );

    for (
      const cita
      of delDia
    ) {

      const slot =
        this.slots.find(
          item =>
            item.hora ===
            cita.hora
        );

      if (slot) {
        slot.cita =
          cita;
      }
    }
  }

  private toISODate(
    d: Date
  ): string {

    const year =
      d.getFullYear();

    const month =
      this.pad2(
        d.getMonth() +
        1
      );

    const day =
      this.pad2(
        d.getDate()
      );

    return (
      `${year}-${month}-${day}`
    );
  }

  private addDaysISO(
    d: Date,
    days: number
  ): string {

    const copy =
      new Date(d);

    copy.setDate(
      copy.getDate() +
      days
    );

    return this.toISODate(
      copy
    );
  }

  private pad2(
    n: number
  ): string {

    return n < 10
      ? '0' + n
      : '' + n;
  }

  badgeClass(
    estado?:
      Cita['estado']
  ): string {

    switch (
    estado
    ) {

      case 'confirmada':
        return 'badge badge-success';

      case 'pendiente':
        return 'badge badge-warning';

      case 'cancelada':
        return 'badge badge-secondary';

      default:
        return 'badge badge-light';
    }
  }

  // ======================================================
  // CONSULTA
  // ======================================================

  obtenerConsulta(
    consulta_id: any
  ): void {

    this.isLoading =
      true;

    this._zoomService
      .getConsulta(
        consulta_id
      )
      .then(
        (resultado: any) => {

          this.consulta =
            resultado;

          if (
            this.consulta
              .tel_doc_app_boo_sta_id ==
            39
          ) {

            Swal.fire({
              allowOutsideClick:
                false,

              html:
                `<i class="material-icons"
                    style="color:#171994;font-size:58px;margin:20px 40px 10px;">
                    mood_bad
                 </i>
                 <br><br>

                 <h1 style="
                    font-family:Mulish;
                    font-weight:800;
                    font-size:28px;
                    text-align:center;
                    color:#171994;">
                    Acceso no autorizado
                 </h1>`,

              showConfirmButton:
                false,

              timer:
                3000
            });

            setTimeout(
              () =>
                this.router
                  .navigate(
                    ['dashboard']
                  ),
              2000
            );

            return;
          }

          this.consulta
            .fecha_consulta =
            this._functions
              .formatApptDate(

                new Date(
                  this.consulta
                    .fecha_consulta
                    .substr(
                      0,
                      10
                    )
                    .replace(
                      /-/g,
                      '/'
                    )
                ),

                this.consulta
                  .hora
              );

          /*
           * Modo prueba solamente
           * para NO OnDemand.
           */
          if (
            this.MODO_PRUEBA_ANULACION &&
            !this.esOnDemand
          ) {

            this.fechaInicioPruebaAnulacion =
              Date.now();

          }

          this.actualizarDisponibilidadAnulacion();

          this.btnIniciar =
            true;

          this.isLoading =
            false;

          this.startObservadorWhenReady();
        }
      )
      .catch(
        error => {

          this.isLoading =
            false;

          this.btnIniciar =
            false;

        }
      );
  }

  // ======================================================
  // VIDEOLLAMADA
  // ======================================================

  async iniciarVideollamada():
    Promise<void> {

    if (!this.consultaOperable || this.videoIniciado) {
      return;
    }

    if (
      this.aceptoInstrucciones
    ) {

      await this.continuarVideollamada();

      return;
    }

    this.mostrarModalInstrucciones =
      true;

    Promise.resolve()
      .then(() =>
        this.abrirModalMedico()
      );
  }

  async continuarVideollamada():
    Promise<void> {

    if (!this.consultaOperable || this.videoIniciado) {
      return;
    }

    if (
      this.aceptoInstrucciones
    ) {

      const key =
        'ocultarModalInstruccionesDoctor' +
        this._usuario._id;

      const valor =
        localStorage
          .getItem(
            key
          );

      if (
        valor !==
        'true'
      ) {

        try {

          await this._schedule
            .postActualizarNoMostrarModalVL();

          localStorage
            .setItem(
              key,
              'true'
            );

        } catch (
        error
        ) {

        }
      }
    }

    this.mostrarModalInstrucciones =
      false;

    this.cerrarOverlay();

    try {

      if (
        !this.WEB_RTC
      ) {
        throw new Error(
          'El componente WEB_RTC no está disponible.'
        );
      }

      await Promise.resolve(
        this.WEB_RTC
          .joinRoom(
            this.consulta
          )
      );

      this.videoIniciado =
        true;

      this.videollamadaFueIniciada =
        true;

      this.videoFinalizada =
        false;

      this.btnIniciar =
        false;

      this.guardarVideollamadaIniciada();

      this.actualizarDisponibilidadAnulacion();

    } catch (
    error
    ) {

      this.videoIniciado =
        false;

      this.videoFinalizada =
        false;

      this.btnIniciar =
        true;

      Swal.fire({
        icon:
          'error',

        title:
          'Error',

        text:
          'No fue posible iniciar la videollamada. Inténtalo nuevamente.',

        confirmButtonColor:
          '#2BABB7'
      });
    }
  }

  private cerrarOverlay():
    void {

    this.overlayRef
      ?.dispose();

    this.overlayRef =
      undefined;
  }

  abrirModalMedico():
    void {

    if (
      this.overlayRef
    ) {
      return;
    }

    this.overlayRef =
      this.overlay.create({

        hasBackdrop:
          true,

        backdropClass:
          'cdk-overlay-dark-backdrop',

        panelClass:
          'modal-instrucciones-doctor-wrapper',

        scrollStrategy:
          this.overlay
            .scrollStrategies
            .block(),

        positionStrategy:
          this.overlay
            .position()
            .global()
            .centerHorizontally()
            .centerVertically()
      });

    const portal =
      new TemplatePortal(
        this.modalInstruccionesTpl,
        this.viewContainerRef
      );

    this.overlayRef
      .attach(
        portal
      );
  }

  cerrarModalMedico():
    void {

    this.mostrarModalInstrucciones =
      false;

    this.cerrarOverlay();
  }

  async finalizarVideollamada():
    Promise<void> {

    if (
      !this.videollamadaFueIniciada ||
      !this.videoIniciado
    ) {
      return;
    }

    this.videoFinalizada =
      true;

    this.videoIniciado =
      false;

    if (
      this._usuario
        .typ_id ==
      1
    ) {

      try {

        await this._zoomService
          .postUpdateEndMeeting({
            id_consulta:
              this.consulta
                .id_consulta
          });

        this.web_rtc_socket
          .socketEmit(

            'endMeetingByDoctor',

            {
              roomName:
                this.consulta
                  .id_consulta
            },

            null
          );

        this.saveMediaIssue(
          this.id_de_consulta ||
          this.consulta
            ?.id_consulta
        );

        /*
         * No limpiamos el estado.
         * Puede reiniciarse la videollamada.
         */
        window.location.reload();

      } catch (
      error
      ) {

        this.videoIniciado =
          true;

        this.videoFinalizada =
          false;

        Swal.fire({
          icon:
            'error',

          title:
            'Error',

          text:
            'No fue posible finalizar la videollamada.',

          confirmButtonColor:
            '#2BABB7'
        });
      }
    }
  }

  // ======================================================
  // CANCEL REASON / ANULACIÓN
  // ======================================================

  mostrarModal(
    tipo: string
  ): void {

    if (tipo === 'salir' && !this.mostrarBotonAnularConsulta) {
      return;
    }

    /*
     * Mientras no se confirme una anulación,
     * el flujo se considera normal.
     */
    this.typeEndSeleccionado =
      1;

    this.cancelReasonSeleccionadoId =
      null;

    this.finalizacionDesdeAnulacion =
      false;

    this.anularConsultaForm
      .reset({

        paciente:
          this._usuario._id,

        id_consulta:
          this.consulta
            .id_consulta,

        motivo:
          null,

        end_description:
          null
      });

    /*
     * Si todavía no existen motivos cargados,
     * volvemos a solicitarlos.
     */
    if (
      tipo === 'salir' &&
      this.cancelReason
        .length ===
      0
    ) {

      Swal.fire({
        allowOutsideClick:
          false,

        title:
          'Espere por favor...',

        showConfirmButton:
          false,

        didOpen:
          () =>
            Swal.showLoading()
      });

      this._schedule
        .getListarCancelReason(
          'CONSULTA-MEDICO'
        )
        .then(
          (response: any) => {

            this.cancelReason =
              response ||
              [];

            Swal.close();

            this.modalActual =
              tipo;

            document.body
              .classList
              .add(
                'modal-open'
              );
          }
        )
        .catch(
          (error: any) => {

            Swal.fire({
              icon:
                'error',

              title:
                'Error',

              text:
                'No fue posible cargar los motivos de finalización.',

              confirmButtonColor:
                '#2BABB7'
            });
          }
        );

      return;
    }

    this.modalActual =
      tipo;

    document.body
      .classList
      .add(
        'modal-open'
      );
  }

  cerrarModal():
    void {

    this.modalActual =
      null;

    this.typeEndSeleccionado =
      1;

    this.cancelReasonSeleccionadoId =
      null;

    this.finalizacionDesdeAnulacion =
      false;

    document.body
      .classList
      .remove(
        'modal-open'
      );
  }

  /*
   * Normaliza textos para poder identificar
   * "Paciente no asiste" sin depender de:
   *
   * - mayúsculas;
   * - minúsculas;
   * - tildes;
   * - espacios extras.
   */
  private normalizarTexto(
    value: any
  ): string {

    return String(
      value ??
      ''
    )
      .normalize(
        'NFD'
      )
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .trim()
      .toUpperCase();
  }

  private obtenerCancelReasonSeleccionado():
    any |
    null {

    const idReason =
      Number(
        this.anularConsultaForm
          .get('motivo')
          ?.value
      );

    if (
      !idReason ||
      !Number.isFinite(
        idReason
      )
    ) {
      return null;
    }

    return (
      this.cancelReason
        .find(
          item =>
            Number(
              item.id_reason
            ) ===
            idReason
        ) ||
      null
    );
  }

  /*
   * REGLA TYPE END:
   *
   * "Paciente no asiste" -> 2
   *
   * Todos los demás cancel_reason -> 3
   */
  private obtenerTypeEndCancelacion():
    2 |
    3 {

    const motivo =
      this.obtenerCancelReasonSeleccionado();

    const nombre =
      this.normalizarTexto(
        motivo
          ?.reason_name
      );

    if (
      nombre ===
      'PACIENTE NO ASISTE'
    ) {
      return 2;
    }

    return 3;
  }

  private getEndDescription():
    string |
    null {

    const value =
      this.anularConsultaForm
        .get(
          'end_description'
        )
        ?.value;

    if (
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const descripcion =
      String(
        value
      )
        .trim();

    return descripcion
      ? descripcion
      : null;
  }

  continuarAnulacion():
    void {

    if (
      this.anularConsultaForm
        .invalid
    ) {

      this.anularConsultaForm
        .markAllAsTouched();

      Swal.fire({
        icon:
          'warning',

        title:
          'Atención',

        text:
          'Por favor selecciona un motivo.',

        confirmButtonColor:
          '#2BABB7'
      });

      return;
    }

    const motivo =
      this.obtenerCancelReasonSeleccionado();

    if (!motivo) {

      Swal.fire({
        icon:
          'warning',

        title:
          'Atención',

        text:
          'El motivo seleccionado no es válido.',

        confirmButtonColor:
          '#2BABB7'
      });

      return;
    }

    /*
     * Guardamos por separado:
     *
     * 1.- cancelReasonSeleccionadoId:
     *     id_reason de cancel_reason.
     *
     * 2.- typeEndSeleccionado:
     *     2 si "Paciente no asiste".
     *     3 cualquier otro motivo.
     */
    this.cancelReasonSeleccionadoId =
      Number(
        motivo.id_reason
      );

    this.typeEndSeleccionado =
      this.obtenerTypeEndCancelacion();

    this.finalizacionDesdeAnulacion =
      true;

    this.modalActual =
      null;

    document.body
      .classList
      .remove(
        'modal-open'
      );

    this.verificacionFinalizar(
      true
    );
  }

  getMotivoAnulacionSeleccionado():
    string {

    const motivo =
      this.obtenerCancelReasonSeleccionado();

    return (
      motivo
        ?.reason_name ||
      ''
    );
  }

  getDescripcionFinalizacion():
    string {

    return (
      this.getEndDescription() ||
      ''
    );
  }

  // ======================================================
  // RESUMEN FICHA
  // ======================================================

  openFinalizarModal():
    void {

    this.modalActual =
      'finalizarFicha';

    document.body
      .classList
      .add(
        'modal-open'
      );
  }

  cerrarFinalizarModal():
    void {

    this.modalActual =
      null;

    document.body
      .classList
      .remove(
        'modal-open'
      );
  }

  volverDesdeResumen():
    void {

    if (
      this.finalizacionDesdeAnulacion
    ) {

      this.modalActual =
        'salir';

      document.body
        .classList
        .add(
          'modal-open'
        );

      return;
    }

    this.cerrarFinalizarModal();
  }

  async verificacionFinalizar(
    desdeAnulacion:
      boolean = false
  ): Promise<void> {

    /*
     * Flujo normal.
     */
    if (
      !desdeAnulacion
    ) {

      this.finalizacionDesdeAnulacion =
        false;

      this.typeEndSeleccionado =
        1;

      this.cancelReasonSeleccionadoId =
        null;
    }

    Swal.fire({
      allowOutsideClick:
        false,

      html:
        `<div style="margin:40px 0;">

          <i class="material-icons"
             style="color:#2BABB7;font-size:58px;margin-bottom:25px">
             schedule
          </i>

          <h1 style="
              font-family:Mulish;
              font-weight:700;
              font-size:24px;
              line-height:32px;
              text-align:center;
              color:#171994;">
              Cargando resumen...
          </h1>

        </div>`,

      showConfirmButton:
        false,

      didOpen:
        () =>
          Swal.showLoading()
    });

    try {

      const [
        archivos,
        documentos
      ] =
        await Promise.all([

          this
            ._fichaMedicaServices
            .getListarFichaConsulta(
              this.consulta
            ),

          this
            ._documentServices
            .postListaArchivosConsulta(
              this.consulta
                .id_consulta,

              this.consulta
                .user_id
            )
        ]);

      this.archivos_consulta =
        archivos ||
        {
          diagnostics: [],
          certificados: [],
          prescriptions: [],
          orders: []
        };

      this.documentos_consulta =
        documentos ||
        [];

      Swal.close();

      this.openFinalizarModal();

    } catch (
    error
    ) {

      Swal.fire({
        icon:
          'error',

        title:
          'Error',

        text:
          'No fue posible cargar el resumen de la ficha médica.',

        confirmButtonColor:
          '#2BABB7'
      });
    }
  }

  finalizarConsultaDesdeResumen(): void {
    if (!this.consultaOperable) {
      return;
    }

    this.cerrarFinalizarModal();

    /*
     * La elección del endpoint depende EXCLUSIVAMENTE
     * de tel_doc_app_boo_is_ondemand.
     *
     * El type_end ya fue definido previamente:
     *
     * 1 = Finalización normal
     * 2 = Paciente no asiste
     * 3 = Otro motivo
     */

    if (this.esOnDemand) {

      // ON DEMAND
      this.finalizarVideoconsultaOd();

    } else {

      // CONSULTA AGENDADA
      this.finalizarVideoconsulta();
    }
  }

  // ======================================================
  // FINALIZAR CONSULTA
  // ======================================================

  finalizarVideoconsulta(): void {

    const params = {
      id_consulta: this.consulta.id_consulta,
      id_consulta_od: this.consulta.tel_doc_app_boo_boo_od_id,
      is_ondemand: this.esOnDemand,
      id_doctor: this.consulta.doc_id,

      /*
       * 1 = Finalización normal
       * 2 = Paciente no asiste
       * 3 = Otro motivo
       */
      type_end: this.typeEndSeleccionado,

      /*
       * Solo existe si viene desde
       * "Anular consulta".
       */
      cancel_reason:
        this.finalizacionDesdeAnulacion
          ? this.cancelReasonSeleccionadoId
          : null,

      /*
       * Campo opcional.
       */
      end_description:
        this.finalizacionDesdeAnulacion
          ? this.getEndDescription()
          : null
    };

    Swal.fire({
      html: `
      <div style="margin:40px 0;">
        <i class="material-icons"
          style="color:#2BABB7;font-size:58px;margin-bottom:25px">
          schedule
        </i>

        <h1 style="
          font-family:Mulish;
          font-weight:700;
          font-size:24px;
          line-height:32px;
          text-align:center;
          color:#171994;">
          Espere por favor...
        </h1>
      </div>
    `,
      showConfirmButton: false,
      allowOutsideClick: false,
      timer: 100000
    });

    Swal.showLoading();

    this._zoomService
      .postUpdateSheduleEndState(params)
      .then(() => {

        this.limpiarEstadoVideollamada();

        const mensaje =
          this.finalizacionDesdeAnulacion
            ? 'La consulta fue finalizada'
            : 'La consulta fue finalizada de forma exitosa';

        Swal.fire({
          html: `
          <i class="material-icons"
            style="color:#171994;font-size:58px;margin:20px 40px 10px;">
            mood
          </i>

          <br>

          <h1 style="
            font-family:Mulish;
            font-weight:800;
            font-size:34px;
            line-height:44px;
            text-align:center;
            color:#171994;">
            ${mensaje}
          </h1>

          <br>
        `,
          showCancelButton: false,
          showConfirmButton: false,
          timer: 1200
        });

        this.router.navigate([
          '/',
          'proximasConsultas'
        ]);

        setTimeout(
          () => window.location.reload(),
          200
        );
      })
      .catch(error => {

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text:
            this.finalizacionDesdeAnulacion
              ? 'No fue posible anular y finalizar la consulta.'
              : 'No fue posible finalizar la consulta.',
          confirmButtonColor: '#2BABB7'
        });
      });
  }

  /*
   * Finalización OnDemand.
   * Tanto la finalización normal como la realizada por un problema
   * utilizan postFinalizarConsultaOnDemand. Si proviene de anulación,
   * además envía cancel_reason y end_description.
   */
  finalizarVideoconsultaOd(): void {

    const params = {
      id_consulta: this.consulta.id_consulta,
      id_consulta_od: this.consulta.tel_doc_app_boo_boo_od_id,
      is_ondemand: this.esOnDemand,
      id_doctor: this.consulta.doc_id,

      /*
       * 1 = Finalización normal
       * 2 = Paciente no asiste
       * 3 = Otro motivo
       */
      type_end: this.typeEndSeleccionado,

      /*
       * Si la finalización fue por problema,
       * enviamos el cancel_reason seleccionado.
       */
      cancel_reason:
        this.finalizacionDesdeAnulacion
          ? this.cancelReasonSeleccionadoId
          : null,

      /*
       * Campo opcional.
       */
      end_description:
        this.finalizacionDesdeAnulacion
          ? this.getEndDescription()
          : null
    };

    Swal.fire({
      html: `
      <div style="margin:40px 0;">
        <i class="material-icons"
          style="color:#2BABB7;font-size:58px;margin-bottom:25px">
          schedule
        </i>

        <h1 style="
          font-family:Mulish;
          font-weight:700;
          font-size:24px;
          line-height:32px;
          text-align:center;
          color:#171994;">
          Espere por favor...
        </h1>
      </div>
    `,
      showConfirmButton: false,
      allowOutsideClick: false,
      timer: 100000
    });

    Swal.showLoading();

    this._zoomService
      .postFinalizarConsultaOnDemand(params)
      .then(() => {

        this.limpiarEstadoVideollamada();

        const mensaje =
          this.finalizacionDesdeAnulacion
            ? 'La consulta fue finalizada'
            : 'La consulta fue finalizada de forma exitosa';

        Swal.fire({
          html: `
          <i class="material-icons"
            style="color:#171994;font-size:58px;margin:20px 40px 10px;">
            mood
          </i>

          <br>

          <h1 style="
            font-family:Mulish;
            font-weight:800;
            font-size:38px;
            line-height:48px;
            text-align:center;
            color:#171994;">
            ${mensaje}
          </h1>

          <br>
        `,
          showCancelButton: false,
          showConfirmButton: false,
          timer: 1000
        });

        this.router.navigate([
          '/',
          'proximasConsultas'
        ]);

        setTimeout(
          () => window.location.reload(),
          200
        );
      })
      .catch(error => {

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text:
            this.finalizacionDesdeAnulacion
              ? 'No fue posible anular y finalizar la consulta.'
              : 'No fue posible finalizar la consulta.',
          confirmButtonColor: '#2BABB7'
        });
      });
  }

  // ======================================================
  // WEBRTC PACIENTE
  // ======================================================

  onMediaError(
    ev: any
  ): void {
  }

  private listenPatientWebRtcStatus():
    void {

    this.web_rtc_socket
      .webrtcUserStatus()
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe(
        (payload: any) => {

          const roomName =
            String(
              payload
                ?.roomName ||
              ''
            );

          const esPaciente =
            payload
              ?.userTypeId ===
            2;

          const esMiSala =
            roomName &&
            (
              roomName ===
              String(
                this.id_de_consulta
              )
              ||
              roomName ===
              String(
                this.consulta
                  ?.id_consulta
              )
            );

          if (
            !esPaciente ||
            !esMiSala
          ) {
            return;
          }

          const mediaErrorCodes = [
            'PATIENT_MEDIA_DENIED',
            'PATIENT_NO_DEVICE',
            'PATIENT_MEDIA_IN_USE',
            'PATIENT_MEDIA_BLOCKED'
          ];

          this.zone.run(
            () => {

              if (
                mediaErrorCodes
                  .includes(
                    payload
                      ?.code
                  )
              ) {

                this.pacienteTieneProblemaMedia =
                  true;

                this.pacienteProblemaMediaCode =
                  payload.code;

                this.pacienteProblemaMediaDetail =
                  payload.detail ||
                  null;

                this.saveMediaIssue(
                  roomName
                );
              }

              if (
                payload?.code ===
                'PATIENT_MEDIA_OK'
                ||
                payload?.code ===
                'PATIENT_WEBRTC_CONNECTED'
              ) {

                this.pacienteTieneProblemaMedia =
                  false;

                this.pacienteProblemaMediaCode =
                  null;

                this.pacienteProblemaMediaDetail =
                  null;

                this.clearMediaIssue(
                  roomName
                );
              }
            }
          );
        }
      );
  }

  private mediaKey(
    roomName: string
  ): string {

    return (
      `webrtc_patient_media_issue_${roomName}`
    );
  }

  private saveMediaIssue(
    roomName: string
  ): void {

    if (
      !roomName
    ) {
      return;
    }

    localStorage
      .setItem(

        this.mediaKey(
          roomName
        ),

        JSON.stringify({
          hasIssue:
            this.pacienteTieneProblemaMedia,

          code:
            this.pacienteProblemaMediaCode,

          detail:
            this.pacienteProblemaMediaDetail,

          ts:
            Date.now()
        })
      );
  }

  private loadMediaIssue(
    roomName: string
  ): void {

    if (
      !roomName
    ) {
      return;
    }

    const raw =
      localStorage.getItem(
        this.mediaKey(
          roomName
        )
      );

    if (
      !raw
    ) {
      return;
    }

    try {

      const parsed =
        JSON.parse(
          raw
        );

      const ttlMs =
        30 *
        60 *
        1000;

      if (
        parsed?.ts &&
        Date.now() -
        parsed.ts >
        ttlMs
      ) {

        localStorage
          .removeItem(
            this.mediaKey(
              roomName
            )
          );

        return;
      }

      this.pacienteTieneProblemaMedia =
        !!parsed?.hasIssue;

      this.pacienteProblemaMediaCode =
        parsed?.code ??
        null;

      this.pacienteProblemaMediaDetail =
        parsed?.detail ??
        null;

    } catch {

      localStorage
        .removeItem(
          this.mediaKey(
            roomName
          )
        );
    }
  }

  private clearMediaIssue(
    roomName: string
  ): void {

    if (
      !roomName
    ) {
      return;
    }

    localStorage
      .removeItem(
        this.mediaKey(
          roomName
        )
      );
  }

  // ======================================================
  // ESTADO VIDEOLLAMADA
  // ======================================================

  private getVideoStartedKey():
    string {

    return (
      `videollamada_doctor_iniciada_${this.id_de_consulta}`
    );
  }

  private getVideoStartedAtKey():
    string {

    return (
      `videollamada_doctor_inicio_${this.id_de_consulta}`
    );
  }

  private guardarVideollamadaIniciada(
    fechaInicio?: Date
  ): void {

    if (!this.id_de_consulta) {
      return;
    }

    const key =
      this.getVideoStartedKey();

    const startedAtKey =
      this.getVideoStartedAtKey();

    /*
     * Marca que esta consulta ya tuvo al menos
     * un inicio de videollamada.
     */
    localStorage.setItem(
      key,
      'true'
    );

    /*
     * Solamente registramos el PRIMER inicio.
     *
     * Si la videollamada se reinicia, este valor
     * no se modifica.
     */
    if (
      !localStorage.getItem(
        startedAtKey
      )
    ) {

      const timestamp =
        fechaInicio &&
          !isNaN(fechaInicio.getTime())
          ? fechaInicio.getTime()
          : Date.now();

      localStorage.setItem(
        startedAtKey,
        timestamp.toString()
      );
    }
  }

  private cargarEstadoVideollamada():
    void {

    if (!this.id_de_consulta) {
      this.videollamadaFueIniciada = false;
      return;
    }

    /*
     * Fallback inmediato mientras llega postMeetingState.
     * La fuente de verdad definitiva se sincroniza luego desde backend.
     */
    this.videollamadaFueIniciada = localStorage.getItem(this.getVideoStartedKey()) === 'true';
  }

  private limpiarEstadoVideollamada():
    void {

    if (
      !this.id_de_consulta
    ) {
      return;
    }

    localStorage
      .removeItem(
        this.getVideoStartedKey()
      );

    localStorage
      .removeItem(
        this.getVideoStartedAtKey()
      );
  }

  sleep(
    ms = 0
  ): Promise<any> {

    return new Promise(
      resolve =>
        setTimeout(
          resolve,
          ms
        )
    );
  }

  // ======================================================
  // CANCEL REASONS
  // ======================================================

  private cargarMotivosCancelacion():
    void {

    this._schedule
      .getListarCancelReason(
        'CONSULTA-MEDICO'
      )
      .then(
        (response: any) => {

          this.cancelReason =
            response ||
            [];

        }
      )
      .catch(
        (error: any) => {

          this.cancelReason =
            [];
        }
      );
  }
}

